import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PrivatePresentationCTA } from "@/components/PrivatePresentationCTA";
import { PRODUCTS } from "@/data/products";
import { useLang } from "@/i18n/LanguageContext";

const Maison = () => {
  const { t } = useLang();
  const m = t.maison;

  return (
    <div data-testid="maison-page">
      {/* INTRO ----------------------------------------------------------- */}
      <section className="pt-16 md:pt-28 pb-24 md:pb-32 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">{m.eyebrow}</p>
          <h1 className="display-hero text-[hsl(var(--md-ink))] max-w-[18ch] leading-[1.02]">
            {m.title}
          </h1>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <p className="editorial text-[hsl(var(--md-ink))]">
                {m.intro1}
              </p>
              <p className="editorial mt-6 text-[hsl(var(--md-ink))]">
                {m.intro2}
              </p>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <p className="editorial-muted">
                {m.introAside}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE PIECE ------------------------------------------------ */}
      <section className="bg-[hsl(var(--md-bone))] border-y border-[hsl(var(--md-line))] py-24 md:py-32 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="md:col-span-6">
              <div className="relative aspect-[4/5] bg-[hsl(var(--md-chalk))] border border-[hsl(var(--md-line))]">
                <img
                  src={PRODUCTS[2].image}
                  alt={m.signatureAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-6">{m.gestureEyebrow}</p>
              <h2 className="display-1 text-[hsl(var(--md-ink))] max-w-[18ch]">
                {m.gestureTitle}
              </h2>
              <p className="editorial mt-8 text-[hsl(var(--md-ink))]">
                {m.gestureBody}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS -------------------------------------------------------- */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <p className="font-eyebrow text-[hsl(var(--md-stone))]">{m.pillarsEyebrow}</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-1 text-[hsl(var(--md-ink))] max-w-[22ch]">
                {m.pillarsTitle}
              </h2>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10">
                {m.pillars.map((b) => (
                  <div key={b.eyebrow}>
                    <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-5">
                      — {b.eyebrow}
                    </p>
                    <h3 className="font-display text-[1.6rem] text-[hsl(var(--md-ink))] leading-tight">
                      {b.title}
                    </h3>
                    <p className="editorial-muted mt-4" dangerouslySetInnerHTML={{ __html: b.body }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSMISSION --------------------------------------------------- */}
      <section className="bg-[hsl(var(--md-sand))]/40 border-y border-[hsl(var(--md-line))] py-28 md:py-44 relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">{m.transmissionEyebrow}</p>
            <h2 className="display-1 text-[hsl(var(--md-ink))]">
              {m.transmissionTitle}
            </h2>
            <p className="editorial mt-10 text-[hsl(var(--md-ink))]">
              {m.transmissionBody}
            </p>
            <Link to="/collection" className="btn-ghost mt-12 inline-flex" data-testid="maison-link-collection">
              {m.transmissionCta}
              <ArrowUpRight size={14} strokeWidth={1.25} />
            </Link>
          </div>
        </div>
      </section>

      <PrivatePresentationCTA />
    </div>
  );
};

export default Maison;
